import { useCallback, useEffect, useRef } from "react";

// addr1 is excluded: it's the field the user is actively typing and is never
// cleared out from under them. Only these autocompleted subfields get the
// stale-value stash/restore treatment.
const RESTORABLE_SUBFIELDS = ["city", "state", "zip"];

/**
 * Clears a stale city/state/zip to "" when the top candidate would *replace* it
 * (so the now-blank field shows the candidate as a ghost), stashing the
 * original and restoring it once no candidate contradicts it anymore.
 *
 * @param {Object} params
 * @param {import("./types").AddressValue} params.value - controlled address.
 * @param {Object|null} params.topCandidate - the candidate the ghosts preview.
 * @param {boolean} params.active - false when autocomplete is dormant
 *   (disabled / verified / skipped); no clearing happens, but a pending stash
 *   still restores since there's then no candidate to ghost with.
 * @param {(delta: Partial<import("./types").AddressValue>) => void} params.onChange
 * @param {(committed: string, candidateValue: string) => boolean} params.isConsistent
 *   "the candidate completes-or-matches this value rather than replacing it."
 * @returns {{ resetStash: (committed?: Object) => void,
 *   userEditedRef: import("react").MutableRefObject<Object> }}
 *   - `resetStash` drops all bookkeeping and re-seeds the per-field baseline to
 *     `committed` (the values a commit is about to write).
 *   - `userEditedRef.current` is the set of subfields the user has typed into
 *     this session (`{ city: true, ... }`).
 */
export default function useStaleSubfieldGhosting({
  value,
  topCandidate,
  active,
  onChange,
  isConsistent,
}) {
  const stashRef = useRef({});
  const userEditedRef = useRef({});
  // The last value this effect itself wrote to each field, so the next run can
  // tell its own clear/restore writes apart from a real user edit.
  const lastSelfWriteRef = useRef({});
  const prevSubfieldRef = useRef({
    city: value.city || "",
    state: value.state || "",
    zip: value.zip || "",
  });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const { city, state, zip } = value;

  const resetStash = useCallback((committed) => {
    stashRef.current = {};
    userEditedRef.current = {};
    lastSelfWriteRef.current = {};
    // Re-seed the edit baseline from the committed values so they aren't misread
    // as user edits next render; resetStash is the only re-seed besides the
    // mount seed, so call it whenever the controlled value changes without a
    // remount (e.g. on commit, so a leftover stash can't restore over it).
    RESTORABLE_SUBFIELDS.forEach((fieldName) => {
      prevSubfieldRef.current[fieldName] =
        (committed && committed[fieldName]) || "";
    });
  }, []);

  useEffect(() => {
    const subfields = { city, state, zip };
    const stash = stashRef.current;
    const edited = userEditedRef.current;
    const selfWrote = lastSelfWriteRef.current;
    const prev = prevSubfieldRef.current;
    const delta = {};

    RESTORABLE_SUBFIELDS.forEach((fieldName) => {
      const current = subfields[fieldName] || "";
      const candidateValue =
        active && topCandidate ? topCandidate[fieldName] || "" : "";

      // A change to a value this effect didn't write (selfWrote) is a real user
      // edit; the selfWrote check is what stops the hook's own clear/restore —
      // which arrives back via `value` next render — from reading as an edit.
      const justEdited =
        current !== prev[fieldName] && current !== selfWrote[fieldName];
      if (justEdited) {
        // Latch the edit so it survives later candidate-driven passes (not just
        // this render), and drop any stash so a restore can't clobber the input.
        edited[fieldName] = true;
        if (stash[fieldName] !== undefined) delete stash[fieldName];
      }

      if (stash[fieldName] !== undefined) {
        // Restore only when no candidate contradicts the stash; a still-
        // conflicting candidate keeps it stashed (the inverse of the clear
        // condition below).
        const candidateAgrees =
          candidateValue && isConsistent(stash[fieldName], candidateValue);
        if (!candidateValue || candidateAgrees) {
          delta[fieldName] = stash[fieldName];
          selfWrote[fieldName] = stash[fieldName];
          delete stash[fieldName];
          delete edited[fieldName];
        }
      } else if (
        !edited[fieldName] &&
        current &&
        candidateValue &&
        !isConsistent(current, candidateValue)
      ) {
        stash[fieldName] = current;
        delta[fieldName] = "";
        selfWrote[fieldName] = "";
      }
    });

    RESTORABLE_SUBFIELDS.forEach((fieldName) => {
      prev[fieldName] =
        delta[fieldName] !== undefined
          ? delta[fieldName]
          : subfields[fieldName] || "";
    });

    if (Object.keys(delta).length) onChangeRef.current(delta);
  }, [topCandidate, city, state, zip, active, isConsistent]);

  return { resetStash, userEditedRef };
}

export { RESTORABLE_SUBFIELDS };
